import React, { useState, useEffect, useRef } from 'react'
import { API_BASE_URL } from '../config/api';
import { Save, FileText, Image, Upload, Trash2, Star } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'
import { Resizable } from 'react-resizable'
import Draggable from 'react-draggable'
import LoadingOverlay from '../components/LoadingOverlay'
import HelpButton from '../components/HelpButton'
import { usePageTour } from '../hooks/usePageTour'
import { TOUR_MODAL_SIGNATURE_CREATE } from '../config/tourSteps'
import 'react-resizable/css/styles.css'

/** Pasos del tour modal que requieren modo texto (firma escrita). */
const SIGNATURE_TOUR_TEXT_TARGETS = new Set([
  'tour-signature-modal-header',
  'tour-signature-type-selector',
  'tour-signature-type-text',
  'tour-signature-text-input',
  'tour-signature-fonts',
])
/** Pasos del tour modal que requieren modo imagen. */
const SIGNATURE_TOUR_IMAGE_TARGETS = new Set([
  'tour-signature-type-image',
  'tour-signature-image-upload',
])

const SignatureConfig = () => {
  const { user } = useAuth()
  const [signatures, setSignatures] = useState([])
  const [defaultSignature, setDefaultSignature] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingConfig, setLoadingConfig] = useState(true)
  usePageTour('signature-config-page', { enabled: !loadingConfig })
  const [showAddForm, setShowAddForm] = useState(false)
  const [newSignature, setNewSignature] = useState({
    signature_type: 'texto',
    signature_text: '',
    signature_image_path: null,
    signature_image_file: null,
    signature_font: 'Dancing Script', // Fuente predeterminada
    image_scale: 50 // Escala de la imagen en porcentaje (50% por defecto)
  })
  
  // Fuentes disponibles para firmas de texto
  const availableFonts = [
    { name: 'Dancing Script', family: 'Dancing Script', label: 'Dancing Script (Elegante)' },
    { name: 'Pacifico', family: 'Pacifico', label: 'Pacifico (Casual)' },
    { name: 'Great Vibes', family: 'Great Vibes', label: 'Great Vibes (Clásica)' },
    { name: 'Satisfy', family: 'Satisfy', label: 'Satisfy (Cursiva)' },
    { name: 'Kalam', family: 'Kalam', label: 'Kalam (Manuscrita)' }
  ]
  const [imagePreview, setImagePreview] = useState(null)
  const [imageContainerSize, setImageContainerSize] = useState({ width: 200, height: 100 })
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 })

  // Cargar tamaño guardado cuando hay una imagen existente
  useEffect(() => {
    if (newSignature.signature_type === 'imagen' && newSignature.signature_image_path && !imagePreview) {
      // Buscar la firma en la lista de firmas para obtener el tamaño guardado
      const existingSignature = signatures.find(sig => 
        sig.signature_image_path === newSignature.signature_image_path
      )
      if (existingSignature && existingSignature.signature_image_width && existingSignature.signature_image_height) {
        setImageContainerSize({
          width: existingSignature.signature_image_width,
          height: existingSignature.signature_image_height
        })
        console.log('📏 Tamaño cargado de firma existente:', {
          width: existingSignature.signature_image_width,
          height: existingSignature.signature_image_height
        })
      }
    }
  }, [newSignature.signature_image_path, signatures, newSignature.signature_type, imagePreview])

  // Debug: Log cuando hay cambios en imagePreview o signature_image_path
  useEffect(() => {
    if (newSignature.signature_type === 'imagen' && (imagePreview || newSignature.signature_image_path)) {
      console.log('🔍 Estado de imagen actualizado:', {
        hasPreview: !!imagePreview,
        hasPath: !!newSignature.signature_image_path,
        imageScale: newSignature.image_scale,
        signatureType: newSignature.signature_type,
        containerSize: imageContainerSize
      })
    }
  }, [imagePreview, newSignature.signature_image_path, newSignature.image_scale, newSignature.signature_type, imageContainerSize])

  // Usar useRef para mantener referencia al estado actual de loadingConfig
  const loadingConfigRef = useRef(loadingConfig)
  
  // Actualizar la referencia cuando cambia el estado
  useEffect(() => {
    loadingConfigRef.current = loadingConfig
  }, [loadingConfig])

  // Tour encadenado: abrir/cerrar formulario solo por eventos del tour (TourManager).
  useEffect(() => {
    const resetFormLikeCancel = () => {
      setNewSignature({
        signature_type: 'texto',
        signature_text: '',
        signature_image_path: null,
        signature_image_file: null,
        signature_font: 'Dancing Script',
        image_scale: 50,
      })
      setImagePreview(null)
      setImageContainerSize({ width: 200, height: 100 })
      setImagePosition({ x: 0, y: 0 })
    }
    const onOpenFromTour = () => {
      resetFormLikeCancel()
      setShowAddForm(true)
    }
    const onCloseFromTour = () => {
      setShowAddForm(false)
      resetFormLikeCancel()
    }
    window.addEventListener('tour:signature-open-create-form', onOpenFromTour)
    window.addEventListener('tour:signature-close-create-form', onCloseFromTour)
    return () => {
      window.removeEventListener('tour:signature-open-create-form', onOpenFromTour)
      window.removeEventListener('tour:signature-close-create-form', onCloseFromTour)
    }
  }, [])

  // Solo con tour `signature-create-modal` activo: alternar texto / imagen según el paso.
  useEffect(() => {
    const onTourStep = (e) => {
      const { tourId, stepIndex } = e.detail || {}
      if (tourId !== 'signature-create-modal' || !showAddForm) return
      const targetId = TOUR_MODAL_SIGNATURE_CREATE.steps[stepIndex]?.targetId
      if (!targetId) return
      if (SIGNATURE_TOUR_TEXT_TARGETS.has(targetId)) {
        setNewSignature((prev) =>
          prev.signature_type !== 'texto'
            ? {
                ...prev,
                signature_type: 'texto',
                signature_image_path: null,
                signature_image_file: null,
                image_scale: 50,
              }
            : prev
        )
        setImagePreview(null)
      }
      if (SIGNATURE_TOUR_IMAGE_TARGETS.has(targetId)) {
        setNewSignature((prev) =>
          prev.signature_type !== 'imagen'
            ? {
                ...prev,
                signature_type: 'imagen',
                signature_text: '',
                image_scale: prev.image_scale || 50,
              }
            : prev
        )
      }
    }
    window.addEventListener('tour:step', onTourStep)
    return () => window.removeEventListener('tour:step', onTourStep)
  }, [showAddForm])

  useEffect(() => {
    loadSignatures()
    
    // Timeout de seguridad: si después de 12 segundos sigue cargando, forzar cierre
    // Aumentado a 12 segundos para dar tiempo al timeout de la petición (8 segundos) + procesamiento
    // Usar ref para verificar el estado actual en lugar del valor capturado en el closure
    const timeout = setTimeout(() => {
      if (loadingConfigRef.current) {
        console.warn('⚠️ Timeout de carga: forzando cierre del loader después de 12 segundos')
        setLoadingConfig(false)
      }
    }, 12000) // Aumentado a 12 segundos
    
    return () => clearTimeout(timeout)
  }, [])

  const loadSignatures = async () => {
    try {
      setLoadingConfig(true)
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      
      // Crear un AbortController para timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 8000) // 8 segundos de timeout
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/config/signature`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: controller.signal
        })
        
        clearTimeout(timeoutId)

        if (response.ok) {
          const data = await response.json()
          console.log('Firmas cargadas (raw):', data)
          
          // Manejar tanto el formato nuevo como el antiguo
          let signaturesList = []
          
          if (data.signatures && Array.isArray(data.signatures)) {
            // Formato nuevo: {signatures: [...], default_signature: {...}}
            console.log('Formato nuevo detectado:', data.signatures.length, 'firmas')
            signaturesList = data.signatures
          } else if (data.signature_type || data.signature_text || data.signature_image_path) {
            // Formato antiguo: objeto directo con signature_type, signature_text, etc.
            console.log('Formato antiguo detectado, convirtiendo...')
            const signature = {
              id: data.id || Date.now(),
              signature_type: data.signature_type,
              signature_text: data.signature_text,
              signature_image_path: data.signature_image_path,
              is_default: data.is_default !== undefined ? data.is_default : true,
              created_at: data.created_at || new Date().toISOString(),
              updated_at: data.updated_at || new Date().toISOString()
            }
            signaturesList = [signature]
          }
          
          // CORRECCIÓN CRÍTICA EN FRONTEND: Asegurar que solo UNA firma esté marcada como predeterminada
          const defaultCount = signaturesList.filter(s => s.is_default).length
          console.log(`🔍 Verificación: ${signaturesList.length} firmas totales, ${defaultCount} marcadas como predeterminadas`)
          
          if (defaultCount > 1) {
            console.error(`❌ ERROR FRONTEND: ${defaultCount} firmas marcadas como predeterminadas. Corrigiendo...`)
            // Ordenar por fecha de creación y mantener solo la más antigua como predeterminada
            signaturesList.sort((a, b) => {
              const dateA = new Date(a.created_at || a.updated_at || 0)
              const dateB = new Date(b.created_at || b.updated_at || 0)
              return dateA - dateB
            })
            // Marcar solo la primera como predeterminada
            signaturesList = signaturesList.map((sig, index) => ({
              ...sig,
              is_default: index === 0
            }))
            console.log('✅ Corregido en frontend: Solo la primera firma queda como predeterminada')
            console.log('   Firmas corregidas:', signaturesList.map(s => ({ id: s.id, default: s.is_default })))
          } else if (defaultCount === 0 && signaturesList.length > 0) {
            // Si no hay ninguna predeterminada, establecer la primera como predeterminada
            console.warn('⚠️ ADVERTENCIA FRONTEND: No hay firmas predeterminadas. Estableciendo la primera...')
            signaturesList.sort((a, b) => {
              const dateA = new Date(a.created_at || a.updated_at || 0)
              const dateB = new Date(b.created_at || b.updated_at || 0)
              return dateA - dateB
            })
            signaturesList = signaturesList.map((sig, index) => ({
              ...sig,
              is_default: index === 0
            }))
            console.log('✅ Primera firma establecida como predeterminada en frontend')
          }
          
          // Verificación final: asegurar que solo hay una predeterminada
          const finalDefaultCount = signaturesList.filter(s => s.is_default).length
          if (finalDefaultCount !== 1 && signaturesList.length > 0) {
            console.error(`❌ ERROR CRÍTICO: Después de la corrección, hay ${finalDefaultCount} firmas predeterminadas. Forzando corrección...`)
            signaturesList = signaturesList.map((sig, index) => ({
              ...sig,
              is_default: index === 0
            }))
            console.log('✅ Corrección forzada aplicada')
          }
          
          console.log('📤 Firmas finales procesadas:', signaturesList.map(s => ({ id: s.id, type: s.signature_type, default: s.is_default })))
          setSignatures(signaturesList)
          setDefaultSignature(signaturesList.find(s => s.is_default) || null)
        } else {
          const error = await response.json()
          console.error('Error al cargar firmas:', error)
          toast.error('Error al cargar firmas')
        }
      } catch (error) {
        console.error('Error al cargar firmas:', error)
        
        // Manejar diferentes tipos de errores
        if (error.name === 'AbortError') {
          toast.error('La petición tardó demasiado. Por favor, intenta nuevamente.')
        } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          toast.error('Error de conexión. Verifica tu conexión a internet.')
        } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.')
        } else {
          toast.error('Error al cargar firmas: ' + (error.message || 'Error desconocido'))
        }
      }
    } catch (error) {
      console.error('Error general al cargar firmas:', error)
      toast.error('Error al cargar firmas: ' + (error.message || 'Error desconocido'))
    } finally {
      // Asegurar que siempre se cierre el loader
      setLoadingConfig(false)
    }
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecciona un archivo de imagen válido')
      return
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe exceder 5MB')
      return
    }

    try {
      // Crear preview
      const reader = new FileReader()
      reader.onloadend = () => {
        const previewUrl = reader.result
        setImagePreview(previewUrl)
        console.log('✅ Preview de imagen creado')
        console.log('   image_scale actual:', newSignature.image_scale)
        console.log('   imagePreview establecido:', !!previewUrl)
      }
      reader.readAsDataURL(file)

      // Subir imagen al servidor
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const formData = new FormData()
      formData.append('file', file)

      const uploadResponse = await fetch(`${API_BASE_URL}/api/upload-signature`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json()
        setNewSignature({
          ...newSignature,
          signature_image_path: uploadData.file_path || uploadData.url,
          signature_image_file: file
        })
        // Si hay una firma existente con tamaño guardado, cargarlo
        // Si no, mantener el tamaño actual o usar valores por defecto
        if (!imageContainerSize || imageContainerSize.width === 200 && imageContainerSize.height === 100) {
          // Mantener tamaño por defecto o cargar de firma existente si está disponible
        }
        toast.success('Imagen subida exitosamente')
      } else {
        const error = await uploadResponse.json()
        toast.error(error.detail || 'Error al subir imagen')
      }
    } catch (error) {
      console.error('Error al subir imagen:', error)
      toast.error('Error al subir imagen')
    }
  }

  const handleAddSignature = async () => {
    // Verificar límite de 5 firmas
    if (signatures.length >= 5) {
      toast.error('Has alcanzado el límite máximo de 5 firmas. Elimina una firma antes de agregar otra.')
      return
    }

    if (newSignature.signature_type === 'texto' && !newSignature.signature_text.trim()) {
      toast.error('Por favor, ingresa el texto de la firma')
      return
    }

    if (newSignature.signature_type === 'imagen' && !newSignature.signature_image_path) {
      toast.error('Por favor, sube una imagen de firma')
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      
      console.log('📤 Agregando nueva firma:', {
        type: newSignature.signature_type,
        hasText: !!newSignature.signature_text,
        hasImage: !!newSignature.signature_image_path,
        currentCount: signatures.length
      })
      
      const response = await fetch(`${API_BASE_URL}/api/config/signature`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          signature_type: newSignature.signature_type,
          signature_text: newSignature.signature_type === 'texto' ? newSignature.signature_text : null,
          signature_image_path: newSignature.signature_type === 'imagen' ? newSignature.signature_image_path : null,
          signature_font: newSignature.signature_type === 'texto' ? newSignature.signature_font : null,
          signature_image_width: newSignature.signature_type === 'imagen' ? imageContainerSize.width : null,
          signature_image_height: newSignature.signature_type === 'imagen' ? imageContainerSize.height : null
        })
      })

      if (response.ok) {
        const result = await response.json()
        console.log('✅ Firma agregada exitosamente:', result)
        toast.success('Firma agregada exitosamente')
        
        // Resetear formulario PRIMERO
        setNewSignature({
          signature_type: 'texto',
          signature_text: '',
          signature_image_path: null,
          signature_image_file: null,
          signature_font: 'Dancing Script',
          image_scale: 50
        })
        setImagePreview(null)
        setImageContainerSize({ width: 200, height: 100 })
        setImagePosition({ x: 0, y: 0 })
        setShowAddForm(false)
        
        // Recargar firmas INMEDIATAMENTE para mostrar todas las firmas
        await loadSignatures()
        
        console.log('📋 Firmas recargadas después de agregar nueva')
      } else {
        const error = await response.json()
        console.error('❌ Error al agregar firma:', error)
        toast.error(error.detail || 'Error al agregar firma')
      }
    } catch (error) {
      console.error('❌ Error al agregar firma:', error)
      toast.error('Error al agregar firma: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSetDefault = async (signatureId, setAsDefault = true) => {
    console.log('handleSetDefault called:', { signatureId, setAsDefault })
    
    const currentSignature = signatures.find(s => s.id === signatureId)
    console.log('Current signature:', currentSignature)
    
    // Si ya es predeterminada y queremos marcarla como predeterminada, no hacer nada
    if (currentSignature && currentSignature.is_default && setAsDefault) {
      console.log('Firma ya es predeterminada, no hacer nada')
      return
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      console.log('Llamando a API con signature_id:', signatureId)
      
      const response = await fetch(`${API_BASE_URL}/api/config/signature/set-default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ signature_id: signatureId })
      })

      console.log('Response status:', response.status)
      
      if (response.ok) {
        const result = await response.json()
        console.log('Response OK:', result)
        toast.success('Firma predeterminada actualizada')
        // Recargar las firmas para actualizar el estado
        await loadSignatures()
      } else {
        const error = await response.json()
        console.error('Error del servidor:', error)
        toast.error(error.detail || 'Error al establecer firma predeterminada')
      }
    } catch (error) {
      console.error('Error al establecer firma predeterminada:', error)
      toast.error('Error al establecer firma predeterminada: ' + error.message)
    }
  }

  const handleDeleteSignature = async (signatureId) => {
    if (!confirm('¿Estás seguro de que deseas eliminar esta firma?')) {
      return
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/config/signature/${signatureId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        toast.success('Firma eliminada exitosamente')
        await loadSignatures()
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al eliminar firma')
      }
    } catch (error) {
      console.error('Error al eliminar firma:', error)
      toast.error('Error al eliminar firma')
    }
  }

  if (loadingConfig) {
    return <LoadingOverlay message="Cargando firmas..." />
  }

  return (
    <div className="min-h-screen" style={{background: 'var(--background-color)'}}>
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="header-left">
            <div id="tour-signature-config-header" className="header-title">
              <h1>Configuración de Firma</h1>
              <p>Gestiona tus firmas personales</p>
            </div>
          </div>
          <div className="header-right">
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Lista de Firmas */}
        <div id="tour-signature-list" className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Mis Firmas</h2>
            <span className="text-sm text-gray-500">
              {signatures.length} / 5 firmas
            </span>
          </div>
          {signatures.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No tienes firmas configuradas. Agrega una para comenzar.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {signatures.map((signature, index) => (
                <div
                  key={signature.id}
                  className={`border-2 rounded-lg p-4 ${
                    signature.is_default
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      {signature.signature_type === 'texto' ? (
                        <FileText className="h-5 w-5 text-primary-600" />
                      ) : (
                        <Image className="h-5 w-5 text-primary-600" />
                      )}
                      <span className="text-sm font-medium text-gray-700">
                        {signature.signature_type === 'texto' ? 'Texto' : 'Imagen'}
                      </span>
                    </div>
                    {signature.is_default && (
                      <span className="text-xs bg-primary-600 text-white px-2 py-1 rounded">
                        Predeterminada
                      </span>
                    )}
                  </div>

                  {signature.signature_type === 'texto' ? (
                    <div className="mb-3">
                      <p 
                        className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2"
                        style={{ 
                          fontFamily: signature.signature_font || 'Dancing Script',
                          fontSize: '30px'
                        }}
                      >
                        {signature.signature_text}
                      </p>
                    </div>
                  ) : (
                    <div className="mb-3">
                      {signature.signature_image_path ? (
                        <div className="flex items-center justify-center">
                          <img
                            src={(() => {
                              let imagePath = signature.signature_image_path
                              // Si ya es una URL completa, usarla tal cual
                              if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
                                return imagePath
                              }
                              // Asegurar que empiece con / para que FastAPI lo sirva correctamente
                              // El backend guarda como "static/signatures/..." y debe servirse como "/static/signatures/..."
                              if (!imagePath.startsWith('/')) {
                                imagePath = `/${imagePath}`
                              }
                              // Construir la URL base correctamente
                              // API_BASE_URL puede ser "http://localhost:8000" o "http://localhost:8000/api"
                              let baseUrl = API_BASE_URL
                              // Si termina con /api, removerlo para archivos estáticos
                              if (baseUrl.endsWith('/api')) {
                                baseUrl = baseUrl.slice(0, -4)
                              }
                              // Asegurar que no termine con /
                              if (baseUrl.endsWith('/')) {
                                baseUrl = baseUrl.slice(0, -1)
                              }
                              const finalUrl = `${baseUrl}${imagePath}`
                              console.log(`[SignatureConfig] Construyendo URL de imagen: ${signature.signature_image_path} -> ${finalUrl}`)
                              return finalUrl
                            })()}
                            alt="Firma"
                            className="max-w-full max-h-32 mx-auto object-contain border border-gray-200 rounded bg-gray-50 p-2"
                            onError={(e) => {
                              console.error('❌ Error al cargar imagen:', signature.signature_image_path)
                              console.error('   URL intentada:', e.target.src)
                              
                              // Si estamos en desarrollo (localhost) y el archivo no existe localmente,
                              // intentar cargar desde producción
                              const currentSrc = e.target.src
                              if (currentSrc.includes('localhost:8000')) {
                                // Intentar cargar desde producción
                                const productionUrl = currentSrc.replace('http://localhost:8000', 'https://pdfsigner.indumapps.com.co')
                                console.log('🔄 Intentando cargar desde producción:', productionUrl)
                                
                                // Crear una nueva imagen para intentar cargar desde producción
                                const img = new Image()
                                img.onload = () => {
                                  console.log('✅ Imagen cargada desde producción')
                                  e.target.src = productionUrl
                                }
                                img.onerror = () => {
                                  console.error('❌ Imagen tampoco disponible en producción')
                                  // Ocultar la imagen y mostrar error
                                  e.target.style.display = 'none'
                                  const errorDiv = e.target.nextElementSibling
                                  if (errorDiv) {
                                    errorDiv.style.display = 'block'
                                  }
                                }
                                img.src = productionUrl
                                return
                              }
                              
                              // Si ya intentamos todas las alternativas, ocultar la imagen y mostrar error
                              e.target.style.display = 'none'
                              const errorDiv = e.target.nextElementSibling
                              if (errorDiv) {
                                errorDiv.style.display = 'block'
                              }
                            }}
                            onLoad={(e) => {
                              console.log('✅ Imagen cargada exitosamente:', signature.signature_image_path)
                              console.log('   URL final:', e.target.src)
                            }}
                          />
                          <div className="hidden text-center text-gray-400 text-sm py-4 border border-gray-200 rounded bg-gray-50 p-4">
                            <p className="text-red-600 font-semibold">⚠️ Imagen no disponible</p>
                            <p className="text-xs mt-2 text-gray-500">El archivo de imagen no se encuentra en el servidor.</p>
                            <p className="text-xs mt-1 text-gray-400 break-all">{signature.signature_image_path}</p>
                            <p className="text-xs mt-2 text-gray-500">Puedes eliminar esta firma y crear una nueva con una imagen válida.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 text-sm py-4">
                          No hay imagen disponible
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200">
                    <label
                      id={index === 0 ? 'tour-signature-default' : undefined}
                      className="flex items-center space-x-2 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={signature.is_default}
                        onChange={async (e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          
                          // Si ya está marcado como predeterminada, no permitir desmarcarlo
                          // (debe marcar otra firma primero)
                          if (signature.is_default) {
                            toast('Para cambiar la firma predeterminada, marca otra firma primero', { icon: 'ℹ️' })
                            return
                          }
                          
                          // Si NO está marcado, marcarlo como predeterminada
                          // Actualizar estado local INMEDIATAMENTE para desmarcar todos y marcar este
                          setSignatures(prev => prev.map(s => ({
                            ...s,
                            is_default: s.id === signature.id
                          })))
                          
                          // Llamar a la API para sincronizar con el backend
                          await handleSetDefault(signature.id, true)
                        }}
                        className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
                        title={signature.is_default 
                          ? 'Esta firma está marcada como predeterminada. Marca otra firma para cambiar la predeterminada.' 
                          : 'Marcar como predeterminada (se desmarcará la actual)'}
                      />
                      <span className={`text-sm font-medium ${signature.is_default ? 'text-primary-600' : 'text-gray-700 group-hover:text-primary-600'}`}>
                        {signature.is_default ? 'Predeterminada' : 'Marcar como predeterminada'}
                      </span>
                    </label>
                    <button
                      id={index === 0 ? 'tour-signature-delete' : undefined}
                      onClick={() => handleDeleteSignature(signature.id)}
                      className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                      disabled={signature.is_default && signatures.length === 1}
                      title={signature.is_default && signatures.length === 1 ? 'No puedes eliminar la única firma predeterminada' : 'Eliminar firma'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botón Agregar Firma */}
        <div className="mb-6 flex justify-center">
          <button
            id="tour-signature-add"
            onClick={() => {
              if (signatures.length >= 5) {
                toast.error('Has alcanzado el límite máximo de 5 firmas. Elimina una firma antes de agregar otra.')
                return
              }
              setShowAddForm(!showAddForm)
            }}
            className="btn-primary flex items-center justify-center gap-2 px-6 py-3"
            disabled={signatures.length >= 5}
          >
            <Upload className="h-5 w-5" />
            <span>Agregar Firma {signatures.length >= 5 && '(Límite alcanzado)'}</span>
          </button>
        </div>

        {/* Formulario para Agregar Firma */}
        {showAddForm && (
          <div className="card">
            <h2 id="tour-signature-modal-header" className="text-xl font-semibold text-gray-900 mb-4">Agregar Nueva Firma</h2>
            <div className="space-y-6">
              {/* Tipo de Firma */}
              <div id="tour-signature-type-selector">
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Tipo de Firma
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    id="tour-signature-type-text"
                    type="button"
                    onClick={() => setNewSignature({ ...newSignature, signature_type: 'texto', signature_image_path: null, signature_image_file: null, image_scale: 50 })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      newSignature.signature_type === 'texto'
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <FileText className={`h-8 w-8 mx-auto mb-2 ${
                      newSignature.signature_type === 'texto' ? 'text-primary-600' : 'text-gray-400'
                    }`} />
                    <p className={`font-medium ${
                      newSignature.signature_type === 'texto' ? 'text-primary-600' : 'text-gray-600'
                    }`}>
                      Firma con Texto
                    </p>
                  </button>

                  <button
                    id="tour-signature-type-image"
                    type="button"
                    onClick={() => setNewSignature({ ...newSignature, signature_type: 'imagen', signature_text: '', image_scale: newSignature.image_scale || 50 })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      newSignature.signature_type === 'imagen'
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Image className={`h-8 w-8 mx-auto mb-2 ${
                      newSignature.signature_type === 'imagen' ? 'text-primary-600' : 'text-gray-400'
                    }`} />
                    <p className={`font-medium ${
                      newSignature.signature_type === 'imagen' ? 'text-primary-600' : 'text-gray-600'
                    }`}>
                      Firma con Imagen
                    </p>
                  </button>
                </div>
              </div>

              {/* Configuración de Firma con Texto */}
              {newSignature.signature_type === 'texto' && (
                <div className="space-y-4">
                  <div id="tour-signature-text-input">
                    <label htmlFor="signature_text" className="block text-sm font-medium text-gray-700 mb-2">
                      Texto de la Firma
                    </label>
                    <input
                      id="signature_text"
                      type="text"
                      value={newSignature.signature_text}
                      onChange={(e) => setNewSignature({ ...newSignature, signature_text: e.target.value })}
                      className="input-field"
                      placeholder={`${user?.nombre || ''} ${user?.apellido || ''}`.trim() || 'Tu nombre'}
                      required
                    />
                  </div>
                  
                  <div id="tour-signature-fonts">
                    <label htmlFor="signature_font" className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Letra
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {availableFonts.map((font) => (
                        <button
                          key={font.name}
                          type="button"
                          onClick={() => setNewSignature({ ...newSignature, signature_font: font.family })}
                          className={`p-4 border-2 rounded-lg transition-all text-left ${
                            newSignature.signature_font === font.family
                              ? 'border-primary-600 bg-primary-50'
                              : 'border-gray-300 hover:border-gray-400 bg-white'
                          }`}
                        >
                          <div 
                            className="text-2xl font-bold mb-2"
                            style={{ fontFamily: font.family }}
                          >
                            {newSignature.signature_text || 'Tu Nombre'}
                          </div>
                          <p className={`text-xs ${
                            newSignature.signature_font === font.family ? 'text-primary-600 font-semibold' : 'text-gray-600'
                          }`}>
                            {font.label}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Configuración de Firma con Imagen */}
              {newSignature.signature_type === 'imagen' && (
                <div id="tour-signature-image-upload">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Imagen de la Firma
                  </label>
                  <div className="space-y-4">
                    {(imagePreview || newSignature.signature_image_path) ? (
                      <div className="space-y-3">
                        <div className="relative" style={{ minHeight: '200px', width: '100%', border: '2px dashed #d1d5db', borderRadius: '8px', padding: '20px', backgroundColor: '#f9fafb', position: 'relative', overflow: 'hidden' }}>
                          <Draggable
                            handle=".drag-handle"
                            position={imagePosition}
                            onStop={(e, data) => {
                              setImagePosition({ x: data.x, y: data.y })
                            }}
                            bounds="parent"
                            cancel=".react-resizable-handle"
                          >
                            <div style={{ position: 'absolute', left: imagePosition.x, top: imagePosition.y }}>
                              <Resizable
                                width={imageContainerSize.width}
                                height={imageContainerSize.height}
                                minConstraints={[80, 40]}
                                maxConstraints={[600, 400]}
                                onResize={(e, { size, handle }) => {
                                  // Actualizar en tiempo real durante el resize
                                  e.preventDefault()
                                  setImageContainerSize({ width: size.width, height: size.height })
                                }}
                                onResizeStop={(e, { size }) => {
                                  // Asegurar que se actualice al final también
                                  setImageContainerSize({ width: size.width, height: size.height })
                                }}
                                resizeHandles={['se']}
                              >
                                <div
                                  className="drag-handle"
                                  style={{
                                    width: `${imageContainerSize.width}px`,
                                    height: `${imageContainerSize.height}px`,
                                    border: '2px solid #3b82f6',
                                    borderRadius: '4px',
                                    backgroundColor: 'white',
                                    cursor: 'move',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    touchAction: 'none',
                                    boxSizing: 'border-box',
                                    minWidth: '80px',
                                    minHeight: '40px'
                                  }}
                                >
                                  <img
                                    src={imagePreview || (newSignature.signature_image_path ? (() => {
                                      let imagePath = newSignature.signature_image_path
                                      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
                                        return imagePath
                                      }
                                      if (!imagePath.startsWith('/')) {
                                        imagePath = `/${imagePath}`
                                      }
                                      const baseUrl = API_BASE_URL.replace('/api', '')
                                      return `${baseUrl}${imagePath}`
                                    })() : '')}
                                    alt="Vista previa de firma"
                                    style={{ 
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      pointerEvents: 'none',
                                      userSelect: 'none'
                                    }}
                                    draggable={false}
                                    onError={(e) => {
                                      const currentSrc = e.target.src
                                      // Si estamos en desarrollo (localhost) y el archivo no existe localmente,
                                      // intentar cargar desde producción
                                      if (currentSrc.includes('localhost:8000')) {
                                        const productionUrl = currentSrc.replace('http://localhost:8000', 'https://pdfsigner.indumapps.com.co')
                                        console.log('🔄 Intentando cargar desde producción (preview):', productionUrl)
                                        
                                        const img = new Image()
                                        img.onload = () => {
                                          console.log('✅ Imagen cargada desde producción (preview)')
                                          e.target.src = productionUrl
                                        }
                                        img.onerror = () => {
                                          console.error('❌ Imagen tampoco disponible en producción (preview)')
                                        }
                                        img.src = productionUrl
                                      }
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '4px',
                                      left: '4px',
                                      backgroundColor: 'rgba(59, 130, 246, 0.9)',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      pointerEvents: 'none',
                                      userSelect: 'none',
                                      fontFamily: 'monospace'
                                    }}
                                  >
                                    {Math.round(imageContainerSize.width)} × {Math.round(imageContainerSize.height)}
                                  </div>
                                </div>
                              </Resizable>
                            </div>
                          </Draggable>
                        </div>
                        <div className="text-sm text-gray-600 space-y-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <p className="font-semibold text-blue-900">📐 Instrucciones de uso:</p>
                          <ul className="list-disc list-inside space-y-1 text-xs text-blue-800">
                            <li><strong>Mover:</strong> Haz clic y arrastra la imagen (área con borde azul) para reposicionarla</li>
                            <li><strong>Redimensionar:</strong> Arrastra la esquina inferior derecha (cuadro azul) para cambiar el tamaño</li>
                            <li><strong>Proporción:</strong> La imagen mantiene automáticamente su proporción original</li>
                            <li><strong>Táctil:</strong> Funciona también en dispositivos táctiles (tablets, móviles)</li>
                          </ul>
                        </div>
                      </div>
                    ) : null}
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-2 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Haz clic para subir</span> o arrastra y suelta
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG o GIF (máx. 5MB)</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Botones */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  id="tour-signature-cancel"
                  onClick={() => {
                    setShowAddForm(false)
                    setNewSignature({
                      signature_type: 'texto',
                      signature_text: '',
                      signature_image_path: null,
                      signature_image_file: null,
                      signature_font: 'Dancing Script',
                      image_scale: 50
                    })
                    setImagePreview(null)
                    setImageContainerSize({ width: 200, height: 100 })
                    setImagePosition({ x: 0, y: 0 })
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  id="tour-signature-save"
                  onClick={handleAddSignature}
                  disabled={loading}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Save className="h-5 w-5" />
                  <span>{loading ? 'Guardando...' : 'Guardar Firma'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Información */}
        <div id="tour-signature-note" className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
          <p className="text-sm text-blue-800 mb-2">
            <strong>Nota:</strong> La firma marcada como predeterminada se usará automáticamente cuando hagas clic en 
            "Agregar Firma" al firmar un documento.
          </p>
          <p className="text-sm text-blue-800">
            <strong>¿Cómo seleccionar una firma predeterminada?</strong> Marca el <strong>checkbox</strong> 
            en la firma que quieras usar por defecto. Solo una firma puede estar marcada como predeterminada a la vez.
          </p>
        </div>

        <HelpButton tourId="signature-config-page" id="tour-signature-help" />
      </div>
    </div>
  )
}

export default SignatureConfig
